// Error al guardar una promo de referido: el mensaje del toast y, si el servidor nombró el campo
// (`data.field`), en qué campo del formulario se marca.
import { ApiError } from '@/api/errors';
import { COST_CENTER_MESSAGES } from '@/shared/lib/costCenter/costCenter';
import type { RetailPromotionsErrorCode } from '../api/promotionsContract';
import { REFERRAL_FORM_MESSAGES } from './referralPromotionForm';
import { retailerScopeErrorCopy } from './retailerScopeError';
import { INVALID_PROMOTION_FALLBACK } from './salePromotionSaveError';
import {
    PAST_VALID_UNTIL,
    SALE_PROMOTION_FIELD_LABELS,
    serverFieldMessage,
} from './serverFieldErrors';

export const TRIGGER_NOT_OF_CHANNEL = 'Ese evento no existe en este tipo de promo. Elige otro.';
export const REWARD_NOT_ALLOWED = 'Ese premio no aplica aquí. Elige otro.';
export const HAS_LEVERS_MESSAGE =
    'La promo de referido no lleva palancas de venta. Recarga la página y guarda de nuevo.';
export const CHANGE_KIND_MESSAGE = 'Una promo no cambia de tipo. Crea una promo de referido nueva.';

const CODE = {
    trigger: 'INVALID_REFERRAL_TRIGGER',
    needsPurchase: 'REFERRAL_REWARD_NEEDS_PURCHASE',
    invalid: 'INVALID_SALE_PROMOTION',
} as const satisfies Record<string, RetailPromotionsErrorCode>;

export interface ReferralFieldError {
    /** Ruta del campo en `ReferralPromotionFormValues`. */
    path: string;
    message: string;
}

export interface ReferralSaveError {
    message: string;
    field?: ReferralFieldError;
}

interface FieldRule {
    path: string;
    /** Con etiqueta, el toast dice de qué campo habla el mensaje. */
    label?: string;
    message: string;
}

const ROLE_LABEL = { referrerReward: 'quien recomienda', referredReward: 'el recomendado' };

/** Campos del bloque `referral` y de la promo que tienen dónde marcarse. */
function ruleOf(field: string, serverMessage: string): FieldRule | null {
    const M = REFERRAL_FORM_MESSAGES;
    const reward = /^referral\.(referrerReward|referredReward)\.(.+)$/.exec(field);
    if (reward) {
        const [, role, leaf] = reward as unknown as [string, keyof typeof ROLE_LABEL, string];
        const of = ROLE_LABEL[role];
        const rewardRules: Record<string, FieldRule> = {
            type: { path: `${role}.type`, message: REWARD_NOT_ALLOWED },
            amountCents: { path: `${role}.amount`, label: `Bono de ${of}`, message: M.money },
            percentBps: {
                path: `${role}.percent`,
                label: `Porcentaje de ${of}`,
                message: M.percent,
            },
            maxAmountCents: { path: `${role}.maxAmount`, label: `Tope de ${of}`, message: M.money },
            'coupon.validityDays': {
                path: `${role}.couponDays`,
                label: `Días del cupón de ${of}`,
                message: M.count,
            },
        };
        if (leaf.startsWith('coupon') && !Object.hasOwn(rewardRules, leaf))
            return { path: `${role}.couponValue`, label: `Cupón de ${of}`, message: M.money };
        return Object.hasOwn(rewardRules, leaf) ? rewardRules[leaf] : null;
    }
    const rules: Record<string, FieldRule> = {
        name: { path: 'name', message: M.name },
        'referral.minAmountCents': { path: 'minAmount', label: 'Monto mínimo', message: M.money },
        'referral.windowDays': { path: 'windowDays', label: 'Ventana', message: M.count },
        'referral.maxRewardsPerReferrer': {
            path: 'maxRewardsPerReferrer',
            label: 'Máximo de premios',
            message: M.count,
        },
        budgetMaxCents: { path: 'budgetMax', label: 'Presupuesto tope', message: M.money },
        costCenterId: {
            path: 'costCenterId',
            label: 'Centro de costo',
            message: COST_CENTER_MESSAGES.tooLong,
        },
    };
    if (field === 'validFrom' || field === 'validUntil') {
        if (/posterior/.test(serverMessage)) return { path: 'validUntil', message: M.range };
        if (/ya pasó/.test(serverMessage)) return { path: 'validUntil', message: PAST_VALID_UNTIL };
        return { path: 'validUntil', message: 'Elige una fecha de vigencia válida.' };
    }
    return Object.hasOwn(rules, field) ? rules[field] : null;
}

function fieldOf(error: ApiError): string | null {
    const field = (error.data as { field?: unknown } | null | undefined)?.field;
    return typeof field === 'string' && field !== '' ? field : null;
}

const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

function withField(rule: FieldRule): ReferralSaveError {
    return {
        message: rule.label ? `«${rule.label}»: ${lower(rule.message)}` : rule.message,
        field: { path: rule.path, message: rule.message },
    };
}

function triggerError(field: string | null): ReferralSaveError {
    if (field === 'referral.purchaseNumber' || field === 'referral.installmentStreak') {
        const label = field.endsWith('purchaseNumber') ? 'Compra número' : 'Cupones seguidos';
        return withField({
            path: field.replace('referral.', ''),
            label,
            message: REFERRAL_FORM_MESSAGES.repeat,
        });
    }
    return withField({ path: 'trigger', message: TRIGGER_NOT_OF_CHANNEL });
}

function invalidError(error: ApiError, field: string | null): ReferralSaveError {
    if (field === 'levers') return { message: HAS_LEVERS_MESSAGE };
    if (field === 'referral') return { message: CHANGE_KIND_MESSAGE };
    const rule = field === null ? null : ruleOf(field, error.msg);
    if (rule) return withField(rule);
    return {
        message:
            serverFieldMessage(error, SALE_PROMOTION_FIELD_LABELS) ?? INVALID_PROMOTION_FALLBACK,
    };
}

export function referralSaveError(error: unknown): ReferralSaveError {
    const retailerMessage = retailerScopeErrorCopy(error);
    if (retailerMessage) return { message: retailerMessage };
    if (error instanceof ApiError) {
        const field = fieldOf(error);
        if (error.code === CODE.trigger) return triggerError(field);
        if (error.code === CODE.needsPurchase) {
            const role = field?.replace('referral.', '') ?? 'referrerReward';
            return withField({
                path: `${role}.type`,
                message: REFERRAL_FORM_MESSAGES.needsPurchase,
            });
        }
        if (error.code === CODE.invalid) return invalidError(error, field);
    }
    if (error instanceof Error && error.message) return { message: error.message };
    return { message: 'No pudimos guardar la promo. Intenta de nuevo en un momento.' };
}
